import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import axios from 'axios';
import { authenticateTechnician } from '../services/authService';
import { useTechnicianSessionStore } from '../store/technicianSession';
import { ErrorResponse, LoginPayload } from '../types/auth';
import '../styles/login.css';

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isLoading = useTechnicianSessionStore((state) => state.isLoading);
  const token = useTechnicianSessionStore((state) => state.token);
  const setSession = useTechnicianSessionStore((state) => state.setSession);
  const authMessage = useTechnicianSessionStore((state) => state.authMessage);
  const setAuthMessage = useTechnicianSessionStore((state) => state.setAuthMessage);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const redirectPath = useMemo(() => {
    const state = location.state as { from?: Location } | undefined;
    if (state?.from && 'pathname' in state.from) {
      return state.from.pathname || '/';
    }
    return '/';
  }, [location.state]);

  useEffect(() => {
    if (!isLoading && token) {
      navigate('/', { replace: true });
    }
  }, [isLoading, token, navigate]);

  useEffect(() => {
    if (authMessage) {
      setFormError(authMessage);
      setAuthMessage(null);
    }
  }, [authMessage, setAuthMessage]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    const errors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      errors.email = 'E-Mail-Adresse darf nicht leer sein.';
    }

    if (!password) {
      errors.password = 'Passwort darf nicht leer sein.';
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: LoginPayload = {
        email: email.trim(),
        password
      };
      const response = await authenticateTechnician(payload);
      await setSession(response);
      navigate(redirectPath, { replace: true });
    } catch (error) {
      setPassword('');

      if (axios.isAxiosError<ErrorResponse>(error)) {
        const { response } = error;

        if (response?.status === 401 && response.data?.message) {
          setFormError(response.data.message);
          return;
        }

        if (response?.status === 400 && response.data?.message) {
          setFormError(response.data.message);
          return;
        }

        setFormError('Anmeldung fehlgeschlagen. Bitte erneut versuchen.');
        return;
      }

      setFormError('Unerwarteter Fehler. Bitte später erneut versuchen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="app-loader">Sitzung wird geladen …</div>;
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <header>
          <h1 className="login-card__title">Anmeldung</h1>
          <p className="login-card__subtitle">Bitte mit Inspecta Zugangsdaten anmelden.</p>
        </header>

        {formError && <div className="login-card__alert">{formError}</div>}

        <form className="login-form" onSubmit={handleSubmit} noValidate>
          <div className="login-form__field">
            <label htmlFor="email">E-Mail-Adresse</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="beispiel@inspecta.info"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-invalid={Boolean(fieldErrors.email)}
              aria-describedby={fieldErrors.email ? 'email-error' : undefined}
            />
            {fieldErrors.email && (
              <span id="email-error" className="login-form__error">
                {fieldErrors.email}
              </span>
            )}
          </div>

          <div className="login-form__field">
            <label htmlFor="password">Passwort</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-invalid={Boolean(fieldErrors.password)}
              aria-describedby={fieldErrors.password ? 'password-error' : undefined}
            />
            {fieldErrors.password && (
              <span id="password-error" className="login-form__error">
                {fieldErrors.password}
              </span>
            )}
          </div>

          <button type="submit" className="login-form__submit" disabled={isSubmitting}>
            {isSubmitting ? 'Bitte warten…' : 'Anmelden'}
          </button>
        </form>
      </div>
    </div>
  );
};
